import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIkigai, IkigaiCategory } from '../lib/stores/ikigai';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';

const CATEGORIES = [
  { id: 'love' as IkigaiCategory, title: 'What You Love', emoji: '❤️', description: 'What you are passionate about' },
  { id: 'good_at' as IkigaiCategory, title: 'What You\'re Good At', emoji: '⭐', description: 'Your skills and talents' },
  { id: 'world_needs' as IkigaiCategory, title: 'What the World Needs', emoji: '🌍', description: 'How you can contribute' },
  { id: 'paid_for' as IkigaiCategory, title: 'What You Can Be Paid For', emoji: '💰', description: 'Market value and opportunities' },
];

type ViewMode = 'intro' | 'form' | 'summary';

export default function IkigaiPage() {
  const navigate = useNavigate();
  const { responses, statement, loading, error, initialize, saveAllResponses, saveStatement, initialized } = useIkigai();
  const [viewMode, setViewMode] = useState<ViewMode>('intro');
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState<Record<IkigaiCategory, string[]>>({
    love: ['', '', ''],
    good_at: ['', '', ''],
    world_needs: ['', '', ''],
    paid_for: ['', '', ''],
  });
  const [finalStatement, setFinalStatement] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!initialized) {
      initialize();
    }
  }, [initialized]);

  useEffect(() => {
    if (responses) {
      const newInputs = { ...inputs };
      CATEGORIES.forEach(cat => {
        const catResponses = responses[cat.id] || [];
        newInputs[cat.id] = [
          catResponses[0]?.response || '',
          catResponses[1]?.response || '',
          catResponses[2]?.response || '',
        ];
      });
      setInputs(newInputs);

      // If there's existing data, show summary view
      const hasData = CATEGORIES.some(cat => responses[cat.id]?.length > 0);
      if (hasData && statement) {
        setViewMode('summary');
      }
    }
    if (statement) {
      setFinalStatement(statement.statement);
    }
  }, [responses, statement]);

  const currentCategory = CATEGORIES[step];
  const isOnStatementStep = step === 4;

  const handleNext = async () => {
    if (isOnStatementStep) {
      try {
        await saveStatement(finalStatement);
        setSaveSuccess(true);
        // After saving, show summary view
        setTimeout(() => {
          setViewMode('summary');
          setSaveSuccess(false);
        }, 2000);
      } catch (err) {
        console.error('Save failed:', err);
      }
      return;
    }

    if (step < 3) {
      setStep(step + 1);
    } else {
      // Save all responses before going to statement
      const allResponses: Array<{ category: IkigaiCategory; response: string; sort_order: number }> = [];
      CATEGORIES.forEach(cat => {
        inputs[cat.id].forEach((response, index) => {
          if (response.trim()) {
            allResponses.push({
              category: cat.id,
              response: response.trim(),
              sort_order: index,
            });
          }
        });
      });

      try {
        await saveAllResponses(allResponses);
        setStep(4);
      } catch (err) {
        console.error('Save failed:', err);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const canProceed = () => {
    if (isOnStatementStep) return finalStatement.trim().length > 0;
    return inputs[currentCategory.id].some(input => input.trim().length > 0);
  };

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', width: '100%' }}>
      <style>{`
        .ikigai-summary-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 240px;
          gap: 2rem;
          align-items: center;
        }
        @media (max-width: 768px) {
          .ikigai-summary-layout {
            grid-template-columns: 1fr;
          }
          .ikigai-summary-layout > div:last-child {
            margin: 0 auto;
          }
        }
      `}</style>
      <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '2.5rem',
          fontWeight: 400,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.5rem',
        }}>
          Discover Your Ikigai
        </h1>
        <p style={{ color: 'var(--color-neutral-600)', fontSize: '1.125rem' }}>
          Find your reason for being
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1.5rem',
        }}>
          {error}
        </div>
      )}

      {saveSuccess && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(74, 140, 92, 0.1)',
          border: '1px solid rgba(74, 140, 92, 0.3)',
          borderRadius: '8px',
          color: '#16A34A',
          marginBottom: '1.5rem',
        }}>
          ✓ Saved successfully!
        </div>
      )}

      {viewMode === 'intro' ? (
        <Card style={{ padding: '3rem', maxWidth: '700px' }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🎯</div>
            <h2 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '2rem',
              fontWeight: 500,
              color: 'var(--color-neutral-900)',
              marginBottom: '1rem',
            }}>
              What is Ikigai?
            </h2>
            <p style={{
              color: 'var(--color-neutral-600)',
              fontSize: '1.125rem',
              lineHeight: '1.7',
              marginBottom: '2rem',
            }}>
              Ikigai (生き甲斐) is a Japanese concept meaning "reason for being."
              It's the intersection of what you love, what you're good at, what the world needs,
              and what you can be paid for.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5rem',
            marginBottom: '2.5rem',
          }}>
            <div style={{
              padding: '1.5rem',
              backgroundColor: 'var(--color-primary-50)',
              borderRadius: '12px',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>❤️</div>
              <h3 style={{
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-900)',
              }}>
                What You Love
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'var(--color-neutral-600)',
                lineHeight: '1.5',
              }}>
                Your passions and interests
              </p>
            </div>

            <div style={{
              padding: '1.5rem',
              backgroundColor: 'var(--color-success-50)',
              borderRadius: '12px',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⭐</div>
              <h3 style={{
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-900)',
              }}>
                What You're Good At
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'var(--color-neutral-600)',
                lineHeight: '1.5',
              }}>
                Your skills and talents
              </p>
            </div>

            <div style={{
              padding: '1.5rem',
              backgroundColor: 'var(--color-secondary-50)',
              borderRadius: '12px',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🌍</div>
              <h3 style={{
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-900)',
              }}>
                What the World Needs
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'var(--color-neutral-600)',
                lineHeight: '1.5',
              }}>
                How you can contribute
              </p>
            </div>

            <div style={{
              padding: '1.5rem',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '12px',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💰</div>
              <h3 style={{
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-900)',
              }}>
                What You Can Be Paid For
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'var(--color-neutral-600)',
                lineHeight: '1.5',
              }}>
                Market value and opportunities
              </p>
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <Button onClick={() => setViewMode('form')}>
              Let's Start →
            </Button>
          </div>
        </Card>
      ) : viewMode === 'summary' ? (
        <div>
          {/* Ikigai Statement Card with Venn Diagram */}
          <Card style={{ padding: '2rem', marginBottom: '2rem', minHeight: '280px', display: 'flex', alignItems: 'center' }}>
            <div className="ikigai-summary-layout" style={{ width: '100%' }}>
              {/* Left: Statement */}
              <div>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🎯</div>
                <h2 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '1.5rem',
                  fontWeight: 500,
                  color: 'var(--color-neutral-900)',
                  marginBottom: '0.75rem',
                }}>
                  Your Ikigai
                </h2>
                <p style={{
                  fontSize: '1.125rem',
                  lineHeight: '1.7',
                  color: 'var(--color-neutral-700)',
                  fontStyle: 'italic',
                }}>
                  "{finalStatement}"
                </p>
              </div>

              {/* Right: Venn Diagram */}
              <div style={{ position: 'relative', width: '240px', height: '240px', flexShrink: 0 }}>
                <svg width="240" height="240" viewBox="0 0 300 300" style={{ position: 'absolute', top: 0, left: 0 }}>
                  {/* Background circles */}
                  <circle cx="120" cy="100" r="80" fill="rgba(245, 158, 11, 0.2)" />
                  <circle cx="180" cy="100" r="80" fill="rgba(239, 68, 68, 0.2)" />
                  <circle cx="120" cy="160" r="80" fill="rgba(34, 197, 94, 0.2)" />
                  <circle cx="180" cy="160" r="80" fill="rgba(59, 130, 246, 0.2)" />

                  {/* Center circle (Ikigai) */}
                  <circle cx="150" cy="130" r="35" fill="rgba(245, 158, 11, 0.8)" stroke="var(--color-neutral-800)" strokeWidth="2" />

                  {/* Labels */}
                  <text x="150" y="50" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    What You
                  </text>
                  <text x="150" y="65" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    Love ❤️
                  </text>

                  <text x="250" y="100" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    What You're
                  </text>
                  <text x="250" y="115" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    Good At ⭐
                  </text>

                  <text x="50" y="160" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    What the
                  </text>
                  <text x="50" y="175" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    World Needs 🌍
                  </text>

                  <text x="250" y="200" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    What You Can
                  </text>
                  <text x="250" y="215" textAnchor="middle" fontSize="12" fill="var(--color-neutral-700)" fontWeight="600">
                    Be Paid For 💰
                  </text>

                  {/* Center label */}
                  <text x="150" y="133" textAnchor="middle" fontSize="14" fill="white" fontWeight="700">
                    IKIGAI
                  </text>
                </svg>
              </div>
            </div>
          </Card>

          {/* Four Categories Grid */}
          <div className="grid-standard grid-equal-height" style={{ marginTop: 'var(--space-5)', marginBottom: 'var(--space-8)' }}>
            {CATEGORIES.map(category => {
              const categoryResponses = responses?.[category.id] || [];
              return (
                <Card key={category.id} style={{ padding: '2.5rem', minHeight: '280px' }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>
                      {category.emoji}
                    </div>
                    <h3 style={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: 'var(--color-neutral-900)',
                      marginBottom: '0.5rem',
                    }}>
                      {category.title}
                    </h3>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--color-neutral-600)',
                      marginBottom: '0.25rem',
                    }}>
                      {category.description}
                    </p>
                  </div>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                  }}>
                    {categoryResponses.map((item, index) => (
                      <li key={index} style={{
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        backgroundColor: 'var(--color-neutral-50)',
                        borderRadius: '6px',
                        color: 'var(--color-neutral-800)',
                      }}>
                        {item.response}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setViewMode('form')}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              ✏️ Edit My Ikigai
            </button>
            <Button onClick={() => navigate('/purpose/values')}>
              Next: Core Values →
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
            Step {step + 1} of 5
          </span>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
            {Math.round(((step + 1) / 5) * 100)}% Complete
          </span>
        </div>
        <div style={{
          height: '8px',
          backgroundColor: 'var(--color-neutral-200)',
          borderRadius: '4px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${((step + 1) / 5) * 100}%`,
            backgroundColor: 'var(--color-primary-600)',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {!isOnStatementStep ? (
        <Card style={{ padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              {currentCategory.emoji}
            </div>
            <h2 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '2rem',
              fontWeight: 500,
              color: 'var(--color-neutral-900)',
              marginBottom: '0.5rem',
            }}>
              {currentCategory.title}
            </h2>
            <p style={{ color: 'var(--color-neutral-600)' }}>
              {currentCategory.description}
            </p>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            {inputs[currentCategory.id].map((value, index) => (
              <div key={index} style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                }}>
                  #{index + 1}
                </label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => {
                    const newInputs = { ...inputs };
                    newInputs[currentCategory.id][index] = e.target.value;
                    setInputs(newInputs);
                  }}
                  placeholder={`Enter item ${index + 1}...`}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between' }}>
            <button
              onClick={handleBack}
              disabled={step === 0}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: '8px',
                background: 'white',
                cursor: step === 0 ? 'not-allowed' : 'pointer',
                opacity: step === 0 ? 0.5 : 1,
              }}
            >
              ← Back
            </button>
            <Button onClick={handleNext} disabled={!canProceed() || loading}>
              {loading ? 'Saving...' : step === 3 ? 'Create Statement →' : 'Next →'}
            </Button>
          </div>
        </Card>
      ) : (
        <Card style={{ padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
            <h2 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '2rem',
              fontWeight: 500,
              color: 'var(--color-neutral-900)',
              marginBottom: '0.5rem',
            }}>
              Your Ikigai Statement
            </h2>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '0.5rem',
              color: 'var(--color-neutral-700)',
            }}>
              My Ikigai is...
            </label>
            <textarea
              value={finalStatement}
              onChange={(e) => setFinalStatement(e.target.value)}
              placeholder="Example: My Ikigai is to use my skills to create technology that helps people..."
              rows={6}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: '6px',
                fontSize: '1rem',
                fontFamily: 'inherit',
                lineHeight: '1.6',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between' }}>
            <button
              onClick={handleBack}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
              }}
            >
              ← Back
            </button>
            <Button onClick={handleNext} disabled={!canProceed() || loading}>
              {loading ? 'Saving...' : 'Save Ikigai'}
            </Button>
          </div>
        </Card>
      )}
        </>
      )}
    </div>
  );
}
